import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { cn } from '@/lib/utils';
/**
 * Allowed-tag whitelist for user-supplied campaign descriptions. Mirrors
 * the backend `MarkdownExtension` posture: block raw HTML / images, allow
 * the GFM subset minus tables AND minus headings — campaign descriptions
 * are short copy, not docs. A `# Big Heading` in source becomes plain
 * paragraph text rather than dwarfing the campaign title visually.
 *
 * Critically: `a` tags get `rel` and `target` forced via the renderer
 * override below — `rehype-sanitize` would otherwise pass any
 * author-supplied attribute through.
 */
const sanitizeSchema = {
    ...defaultSchema,
    tagNames: [
        'p', 'br', 'strong', 'em', 'del', 'a',
        'ul', 'ol', 'li',
        'blockquote',
        'code', 'pre',
        // No headings (h1–h6) or `<hr>` — see comment above.
    ],
    attributes: {
        a: ['href', 'title'],
        // No `src` here: images deliberately blocked. Campaigns have a
        // dedicated image field for hero photos.
        code: ['className'],
        pre: ['className'],
    },
};
/**
 * Renders user-supplied markdown with the same safety posture as the
 * backend Twig `|markdown` filter — sanitised tag whitelist, links forced
 * to `rel="noopener nofollow" target="_blank"`. Used in the campaign
 * detail view and the editor's live preview pane.
 */
export function MarkdownPreview({ source, className }) {
    if (!source) {
        return null;
    }
    return (<div className={cn('prose-sm max-w-none text-sm leading-relaxed', 'prose-p:my-0 prose-p:[&+p]:mt-2', 'prose-a:text-cyan-500 prose-a:underline hover:prose-a:text-cyan-400', 'prose-ul:my-1 prose-ul:pl-5 prose-ol:my-1 prose-ol:pl-5', '[&>*]:m-0 [&>*+*]:mt-2 [&_ul]:list-disc [&_ol]:list-decimal', '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs', '[&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2', '[&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:italic', className)}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[[rehypeSanitize, sanitizeSchema]]} components={{
            a: ({ href, children, ...props }) => (<a {...props} href={href} target="_blank" rel="noopener nofollow">
                            {children}
                        </a>),
        }}>
                {source}
            </ReactMarkdown>
        </div>);
}
